import { Button, CloseButton } from '@ui/shared/Button';
import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { exportDateFormatSettingProvider } from '../../../data/providers/settings/exportDateFormatSettingProvider';
import { exportTests } from '../../../data/providers/testsProvider';
import {
  Entity,
  EntityType,
  ISelectOption,
  ITest,
  ITestType,
} from '../../../data/types';
import { saveExportDateFormatSetting } from '../../../data/writers/settings/exportDateFormatSetting';
import { fullDate } from '../../../utils/date';
import {
  buildCSV,
  downloadZipFile,
  makeFilename,
  ValidExportDateFormat,
} from '../../../utils/export';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlLabel } from '../../shared/form/ControlLabel';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { ExportDateFormat } from '../../shared/form/ExportDateFormat';
import { FormContainer } from '../../shared/form/FormContainer';
import { Select } from '../../shared/form/Select';
import { SelectBox } from '../../shared/form/SelectBox';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';

interface IProps {
  closeDialog: () => void;
  tests?: ITest[];
  entity?: Entity;
  entityType?: EntityType;
  sessionId?: number;
}

export const SessionExportPopup = (props: IProps): React.ReactElement => {
  const tests = props.tests || [];

  const averageOptions = [
    { label: 'Tests', value: 'none' },
    { label: 'Averages', value: 'averagesOnly' },
    { label: 'Averages and Tests', value: 'averagesAnd' },
  ];

  const [selectedOption, setSelectedOption] = React.useState(averageOptions[0]);
  const [isDisabled, setIsDisabled] = React.useState(false);

  const [includeInactiveMetrics, setIncludeInactiveMetrics] =
    React.useState<boolean>(false);
  const [deidentify, setDeidentify] = React.useState<boolean>(false);

  const [dateFormat, setDateFormat] =
    React.useState<ValidExportDateFormat>('MM/DD/YYYY');

  const [useDecimalComma, setUseDecimalComma] = React.useState<boolean>(false);

  /**
   * @description subscribe to export date format setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = exportDateFormatSettingProvider.subscribe(
      (value: ValidExportDateFormat): void => setDateFormat(value)
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  const onExport = async (): Promise<void> => {
    let mTests: ITest[];
    if (props.tests) {
      mTests = props.tests;
    } else {
      mTests = await exportTests({
        entityId: props.entity?.id,
        entityType: props.entityType,
        includeInactiveMetrics,
        sessionId: props.sessionId,
        deidentify,
      }).toPromise();
    }

    const sessionDateStart = dayjs
      .utc(
        props.sessionId ? props.sessionId : mTests[mTests.length - 1].sessionId
      )
      .format('MM_DD_YY');

    const entityName = deidentify ? props.entity.id : props.entity.name;

    const testTypes = mTests.map((i: ITest): ITestType => i.testType);
    const data = uniqBy(testTypes, (t: ITestType): string => t.id).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (testType: ITestType): { filename: string; data: any } => {
        const testsOfType = mTests.filter(
          (item: ITest): boolean => item.testType.id === testType.id
        );
        const mFilename = makeFilename(
          `${entityName}-Session-${sessionDateStart}${
            selectedOption.value === 'none'
              ? '-Tests'
              : selectedOption.value === 'averagesOnly'
              ? '-Averages'
              : '-AveragesAndTests'
          }`,
          testType.name
        );
        return buildCSV(
          testsOfType,
          `${mFilename}.csv`,
          dateFormat,
          selectedOption.value,
          useDecimalComma
        );
      }
    );
    const zipNameBase = entityName
      ? `${entityName}-Session-${sessionDateStart}`
      : `Session-${sessionDateStart}`;
    const zipname = `${zipNameBase}-${
      selectedOption.value === 'none'
        ? '-Tests'
        : selectedOption.value === 'averagesOnly'
        ? '-Averages'
        : '-AveragesAndTests'
    }.zip`;

    await downloadZipFile(data, zipname);
    props.closeDialog();
  };

  /**
   * @description starts the download process
   */
  const download = (): void => {
    setIsDisabled(true);
    onExport();
  };

  /**
   * @description handles changes to the desired export format
   * @param e
   */
  const updateSelectedUpdate = (e: ISelectOption): void => {
    setSelectedOption(e);
  };

  /**
   * @description handles user selecting inactive metrics in the export
   */
  const handleInactiveMetricsSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setIncludeInactiveMetrics(e.currentTarget.checked);
  };

  /**
   * @description handles changes to export date format
   * @param value
   */
  const handleExportDateFormatSelection = (
    value: ValidExportDateFormat
  ): void => {
    saveExportDateFormatSetting(value);
  };

  /**
   * @description handles user deidentification selection
   * @param e
   * @returns
   */
  const handleDeidentificationOption = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => setDeidentify(e.currentTarget.checked);

  /**
   * @description handles user selecting use decimal comma in export
   */
  const handleSetUseDecimalComma = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setUseDecimalComma(e.currentTarget.checked);
  };

  return (
    <InnerPopupCard onClose={props.closeDialog} size='small' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>Export Session</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.closeDialog} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <FormContainer style={{ alignItems: 'flex-start' }}>
            <h5>{fullDate(props.sessionId)}</h5>
            <ControlSpacer style={{ width: '100%' }}>
              <ControlLabel>Export Type</ControlLabel>
              <Select
                options={averageOptions}
                value={selectedOption}
                onChange={updateSelectedUpdate}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Include Inactive Metrics'
                selected={includeInactiveMetrics}
                onSelect={handleInactiveMetricsSelection}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Deidentify Data'
                selected={deidentify}
                onSelect={handleDeidentificationOption}
              />
            </ControlSpacer>
            <ControlSpacer>
              <SelectBox
                label='Use Decimal Comma (EUR)'
                selected={useDecimalComma}
                onSelect={handleSetUseDecimalComma}
              />
            </ControlSpacer>
            <ControlSpacer>
              <ExportDateFormat
                onChange={handleExportDateFormatSelection}
                value={dateFormat}
              />
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <Button
              onClick={download}
              type='submit'
              disabled={isDisabled}
              icon='cloud_download'
              title='Export Tests'
              style={{ whiteSpace: 'nowrap' }}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
